import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2 , Printer, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { openPdf } from "@/lib/print";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import { groupJournalEntriesByMonth } from "./journalEntryGroups";

const CAN_MANAGE = ["ORG_ADMIN", "ACCOUNTS"];
const EMPTY_LINE = { account_id: "", debit: "", credit: "", narration: "" };

export default function PostJournal() {
  const { user } = useAuth();
  const canManage = CAN_MANAGE.includes(user?.role);
  const { companies, selectedCompanyId } = useCompany() || {};
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const load = async () => {
    if (!selectedCompanyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [entriesRes, accountsRes] = await Promise.all([
        api.get(`/gl/journal-entries?company_id=${selectedCompanyId}`),
        api.get("/gl/accounts"),
      ]);
      setEntries(entriesRes.data);
      setAccounts(accountsRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [selectedCompanyId]);

  const accountName = (id) => { const a = accounts.find((x) => x.id === id); return a ? `${a.code} — ${a.name}` : id; };
  const entryGroups = groupJournalEntriesByMonth(entries);

  return (
    <div className="space-y-6" data-testid="post-journal-page">
      <PageHeader
        title="Post Journal"
        description="Manual double-entry journal vouchers. Debits must equal credits to post."
        action={selectedCompanyId && (
          <div className="flex flex-wrap gap-2">
            <CashFlowPrintDialog companyId={selectedCompanyId} />
            {canManage && <CreateJournalDialog companyId={selectedCompanyId} accounts={accounts} onCreated={load} />}
          </div>
        )}
      />
      {!selectedCompanyId ? (
        <EmptyState title="Select a company from the top bar first" testId="post-journal-no-company" />
      ) : !loading && entries.length === 0 ? (
        <EmptyState title="No journal entries yet" testId="journal-entries-empty-state" />
      ) : (
        <div className="space-y-6">
          {entryGroups.map((group) => (
            <section key={group.month} className="space-y-3" data-testid={`journal-month-${group.month}`}>
              <h2 className="border-b border-border pb-2 text-lg font-semibold text-foreground">{group.label}</h2>
              <div className="space-y-3">
                {group.entries.map((e) => (
                  <div key={e.id} className="rounded-xl border border-border bg-card p-4" data-testid={`journal-entry-card-${e.id}`}>
                    <div className="flex items-center justify-between text-sm">
                      <button type="button" className="flex items-center gap-2 text-left" onClick={() => toggleExpand(e.id)} data-testid={`journal-toggle-${e.id}`}>
                        {expanded[e.id] ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                        <span className="font-mono text-xs text-muted-foreground">{e.reference || e.source_type}</span>
                        <span className="text-foreground">{e.narration || "—"}</span>
                        <span className="text-[10px] text-muted-foreground/70">{e.lines.length} line{e.lines.length === 1 ? "" : "s"}</span>
                      </button>
                      <span className="flex items-center gap-2 text-xs text-muted-foreground">
                        {e.entry_date}
                        <Button size="sm" variant="ghost" title="Print journal voucher" onClick={() => openPdf(`/print/journal-entries/${e.id}`)} data-testid={`print-journal-${e.id}`}><Printer className="h-3.5 w-3.5" /></Button>
                      </span>
                    </div>
                    {expanded[e.id] && (
                      <Table className="mt-2">
                        <TableHeader><TableRow><TableHead>Account</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {e.lines.map((line) => (
                            <TableRow key={line.id}>
                              <TableCell className="text-muted-foreground">{accountName(line.account_id)}</TableCell>
                              <TableCell className="text-right font-mono text-xs">{Number(line.debit) > 0 ? line.debit : ""}</TableCell>
                              <TableCell className="text-right font-mono text-xs">{Number(line.credit) > 0 ? line.credit : ""}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateJournalDialog({ companyId, accounts, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ entry_date: "", narration: "", reference: "" });
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [lines, setLines] = useState([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const updateLine = (idx, field, value) => setLines(lines.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  const addLine = () => setLines([...lines, { ...EMPTY_LINE }]);
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));

  useEffect(() => {
    if (!open) return;
    api.get(`/masters/branches?company_id=${companyId}`)
      .then(({ data }) => { const active = data.filter((b) => b.is_active); setBranches(active); setBranchId(active.length === 1 ? active[0].id : ""); })
      .catch(() => {});
  }, [open, companyId]);

  const totalDebit = lines.reduce((sum, l) => sum + (Number(l.debit) || 0), 0);
  const totalCredit = lines.reduce((sum, l) => sum + (Number(l.credit) || 0), 0);
  const balanced = totalDebit === totalCredit && totalDebit > 0;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/gl/journal-entries", {
        company_id: companyId, ...form, branch_id: branchId || undefined,
        lines: lines.map((l) => ({ ...l, debit: l.debit || "0", credit: l.credit || "0" })),
      });
      toast.success("Journal entry posted");
      setForm({ entry_date: "", narration: "", reference: "" });
      setLines([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-journal-button"><Plus className="h-4 w-4" /> New Journal Entry</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>New Journal Entry</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-journal-form">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Date</Label>
              <Input required type="date" data-testid="journal-date-input" value={form.entry_date} onChange={set("entry_date")} className="mt-1.5" />
            </div>
            <div className="col-span-2">
              <Label>Narration</Label>
              <Input data-testid="journal-narration-input" value={form.narration} onChange={set("narration")} className="mt-1.5" />
            </div>
          </div>
          {branches.length > 1 && (
            <div>
              <Label>Branch</Label>
              <Select value={branchId} onValueChange={setBranchId}>
                <SelectTrigger className="mt-1.5" data-testid="journal-branch-select"><SelectValue placeholder="Which branch is this entry for?" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Lines</Label>
            <div className="mt-1.5 space-y-2">
              {lines.map((line, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`journal-line-row-${idx}`}>
                  <Select value={line.account_id} onValueChange={(v) => updateLine(idx, "account_id", v)}>
                    <SelectTrigger className="flex-1" data-testid={`journal-line-account-select-${idx}`}><SelectValue placeholder="Account" /></SelectTrigger>
                    <SelectContent>{accounts.filter((a) => !a.is_group).map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" step="0.01" placeholder="Debit" className="w-28" value={line.debit} onChange={(e) => updateLine(idx, "debit", e.target.value)} data-testid={`journal-line-debit-input-${idx}`} />
                  <Input type="number" step="0.01" placeholder="Credit" className="w-28" value={line.credit} onChange={(e) => updateLine(idx, "credit", e.target.value)} data-testid={`journal-line-credit-input-${idx}`} />
                  {lines.length > 2 && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeLine(idx)} data-testid={`remove-journal-line-button-${idx}`}>
                      <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={addLine} data-testid="add-journal-line-button">
              <Plus className="h-3.5 w-3.5" /> Add line
            </Button>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="journal-balance-indicator">
            <span className="text-muted-foreground">Debit: <span className="font-mono text-foreground">{totalDebit.toFixed(2)}</span> · Credit: <span className="font-mono text-foreground">{totalCredit.toFixed(2)}</span></span>
            <span className={balanced ? "font-semibold text-emerald-500" : "font-semibold text-rose-400"}>{balanced ? "Balanced" : "Not balanced"}</span>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving || !balanced || (branches.length > 1 && !branchId)} data-testid="create-journal-submit-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Post entry
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CashFlowPrintDialog({ companyId }) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState({ date_from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), date_to: iso(today) });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="print-cash-flow-button"><Printer className="h-4 w-4" /> Print cash flow</Button></DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Cash flow (Cash &amp; Bank)</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Every receipt and payment through Cash &amp; Bank (1110) between these dates, with opening and closing balance.</p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>From</Label><Input type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} className="mt-1.5" /></div>
          <div><Label>To</Label><Input type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} className="mt-1.5" /></div>
        </div>
        <DialogFooter>
          <Button onClick={() => { setOpen(false); openPdf("/print/cash-book", { company_id: companyId, ...range }); }}><Printer className="h-4 w-4" /> Open PDF</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
